import { MetadataError } from '@sparelabs/core'
import { Geography, IPoint } from '@sparelabs/geography'
import { last } from 'lodash'
import { SlotType } from '../types'
import { ApproximateRoutingCosts } from './ApproximateRoutingCosts'

export interface IBinnableSlot {
  scheduledTs: number
  scheduledLocation: IPoint
  type: SlotType
}

export interface ISlotBin<T> {
  scheduledTs: number
  location: IPoint
  slots: T[]
}

export class SlotBinCalculator {
  // This should be kept consistent with the same value in EquivalentCoordinateCalculator.scala in route-optimization-service
  private static readonly APPROXIMATE_ROUTE_DURATION_THRESHOLD_SECONDS: number = 15

  /**
   * WARNING: If you are going to use this function you need to make sure your list of slots is sorted
   * The notion of bins doesn't makes sense unless the slots are in order
   * And this has led to bugs in the past!
   */
  public static binByGeohash<T extends IBinnableSlot>(slots: T[]): Array<ISlotBin<T>> {
    if (slots.length === 0) {
      return []
    }
    const firstSlot = slots[0]
    this.validateScheduledLocation(firstSlot)

    const slotBins: Array<ISlotBin<T>> = [
      { location: firstSlot.scheduledLocation, scheduledTs: firstSlot.scheduledTs, slots: [firstSlot] },
    ]

    let previousSlot = firstSlot
    for (const slot of slots.slice(1)) {
      const previousBin = last(slotBins) as ISlotBin<T>

      /**
       * This is basically a very simplified version of the binning logic in route-optimization-service. The
       * route-optimization-service version is a lot more nuanced/complex, and in output from route-optimization-service,
       * consecutive slots should only be binned if the have an identical scheduledTs. However, as this class will not
       * always be used on output from route-optimization-service, we have 2 other safeguards:
       * - Same geohash is always considered the same bin (this is part of the ROS logic as well)
       * - And even if they are close in scheduledTs, we just do a sanity check that they're close together in space too
       *
       * Overall, this logic will always be perfectly consistent with ROS' definition if you use it on output from ROS,
       * because the scheduledTs bit will take over, but it will also work decently well on slot lists that aren't direct
       * output from ROS.
       */
      const sameGeohash = this.geohashSlot(slot) === Geography.geohash(previousBin.location)
      const sameScheduledTime = slot.scheduledTs === previousBin.scheduledTs
      const closeTogether =
        ApproximateRoutingCosts.getDuration(slot.scheduledLocation, previousBin.location) <
        this.APPROXIMATE_ROUTE_DURATION_THRESHOLD_SECONDS

      // break slots should go in their own bin
      if (
        (sameGeohash || (sameScheduledTime && closeTogether)) &&
        slot.type !== SlotType.DriverBreak &&
        previousSlot.type !== SlotType.DriverBreak
      ) {
        previousBin.slots.push(slot)
      } else {
        slotBins.push({ location: slot.scheduledLocation, scheduledTs: slot.scheduledTs, slots: [slot] })
      }
      previousSlot = slot
    }

    return slotBins
  }

  private static geohashSlot(slot: IBinnableSlot): string {
    this.validateScheduledLocation(slot)
    return Geography.geohash(slot.scheduledLocation)
  }

  private static validateScheduledLocation<T extends IBinnableSlot>(slot: T): void {
    if (!slot.scheduledLocation) {
      throw new MetadataError('every slot should have a scheduled location', { slot })
    }
  }
}
